/**
 * WHAT:
 *   Main TestTab component orchestrating all sub-components and hooks.
 *
 * WHY:
 *   Provides a clean, maintainable entry point for the PDF parser testing interface.
 *
 * DOMAIN: Investments → Parqet → Test Tab
 *
 * DEPENDENCIES:
 *   - Tauri core API (invoke, listen)
 *   - Tauri plugins (dialog, opener)
 *   - Supabase securities API
 *   - Custom hooks (useTestTabState, useScanProgress, etc.)
 *   - UI components (Card, etc.)
 *
 * RISKS:
 *   - NOTE: Large PDF files may cause memory issues during parsing
 *   - NOTE: Directory scanning with many files may be slow and cause UI blocking
 *   - NOTE: Event listeners may not properly clean up on unmount
 *   - WARN: Yahoo Finance API calls may hit rate limits
 *   - NOTE: Securities data loading may fail silently
 */

import { type FC, useEffect } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";

import {
  TestTabHeader,
  YahooFinanceTest,
  ScanControls,
  ScanResults,
  PdfUploadArea,
  RawPdfTextDisplay,
  RawDataDebugDisplay,
  ParsedDataDisplay,
  ErrorDisplay,
} from "./components";
import {
  useTestTabState,
  useScanProgress,
  useSecuritiesEnrichment,
  usePdfNavigation,
  useFileHandlers,
} from "./hooks";
import { formatNumber2 } from "./utils";
import { isEmpty } from "./utils/validation";

/**
 * WHAT:
 *   Comprehensive test tab component for PDF parser testing with file upload, directory scanning,
 *   validation, import functionality, and Yahoo Finance integration.
 *
 * WHY:
 *   Provides developers and users with a complete testing interface for debugging PDF parsing,
 *   validating parsed data, and importing transactions to Supabase.
 *
 * PROPS:
 *   None - Root component
 *
 * BEHAVIOR:
 *   - Supports single file upload and directory scanning
 *   - Provides real-time progress tracking for scanning operations
 *   - Validates parsed data before import
 *   - Integrates with Yahoo Finance for security enrichment
 *   - Loads securities data from Supabase
 *   - Supports navigation through parsed/failed PDFs
 *   - Displays raw PDF text and parsed data for debugging
 *
 * RISKS:
 *   - NOTE: Component orchestrates multiple hooks - ensure proper cleanup
 *   - NOTE: Event listeners managed by useScanProgress hook
 *   - WARN: Large state object may cause re-render performance issues
 *
 * @returns {JSX.Element} The TestTab component
 *
 */
export const TestTab: FC = () => {
  // Component mounted - only log once
  useEffect(() => {
    debugLog("info", "investments", "TestTab mounted", { module: "parqet" });
  }, []);

  // --- STATE MANAGEMENT ---
  const state = useTestTabState();

  // --- EVENT LISTENERS ---
  useScanProgress(state);

  // --- SECURITIES ENRICHMENT ---
  useSecuritiesEnrichment(state);

  // --- PDF NAVIGATION ---
  const { handleNextPdf, handlePreviousPdf } = usePdfNavigation(state);

  // --- FILE HANDLERS ---
  const {
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileSelect,
    handleScanFolder,
    navigateToNextError,
    navigateToPreviousError,
    openPdfFile,
    processPDF,
    processPDFRaw,
    resetState,
    testYahooEnrichment,
  } = useFileHandlers(state);

  // --- RENDER ---
  return (
    <div
      className="h-full flex flex-col space-y-6 overflow-y-auto parqet-test-scrollbar"
      style={{
        scrollbarColor: "#475569 #1e293b",
        scrollbarWidth: "thin",
      }}
    >
      <style>{`
        .parqet-test-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .parqet-test-scrollbar::-webkit-scrollbar-track {
          background: #1e293b;
          border-radius: 4px;
        }
        .parqet-test-scrollbar::-webkit-scrollbar-thumb {
          background: #475569;
          border-radius: 4px;
        }
        .parqet-test-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
      `}</style>

      {/* Header */}
      <TestTabHeader />

      {/* Yahoo Finance Test Section */}
      <YahooFinanceTest
        identifier={state.yahooIdentifier}
        loading={state.yahooLoading}
        onIdentifierChange={state.setYahooIdentifier}
        onTest={() => {
          void testYahooEnrichment();
        }}
        result={state.yahooResult}
      />

      {/* PDF Parser Section */}
      <div className="bg-slate-700/30 border border-slate-600/50 rounded-lg p-6 shadow-tracq-card">
        <h2 className="text-xl font-semibold text-white mb-4">PDF Parser</h2>

        {/* Scan Controls */}
        <ScanControls
          isProcessing={state.isProcessing}
          onReset={resetState}
          onScanFolder={() => {
            void handleScanFolder();
          }}
          onSkipEnrichmentChange={state.setSkipEnrichment}
          skipEnrichment={state.skipEnrichment}
        />

        {/* Scan Results */}
        {state.scanResults ? (
          <ScanResults
            browseFailedOnly={state.browseFailedOnly}
            currentPdfIndex={state.currentPdfIndex}
            failedPdfs={state.failedPdfs}
            formatNumber2={formatNumber2}
            isLoadingSecurities={state.isLoadingSecurities}
            onNextPdf={handleNextPdf}
            onPreviousPdf={handlePreviousPdf}
            onToggleBrowseMode={(newMode) => {
              state.setBrowseFailedOnly(newMode);
              state.setCurrentPdfIndex(0);
              const list = newMode ? state.failedPdfs : state.parsedPdfs;
              if (list.length > 0) {
                const first = list[0];
                if (!first) {
                  return;
                }
                if (newMode) {
                  // Zu fehlerhaften wechseln
                  state.setRawPdfText(first.raw_text ?? "⚠️ No PDF text extracted");
                  state.setShowRawData(true);
                  state.setError(first.error ?? null);
                  state.setUploadedFile(
                    new File([], first.file_name ?? "unknown.pdf", {
                      type: "application/pdf",
                    }),
                  );
                  state.setRawData(null);
                } else {
                  // Zu erfolgreichen wechseln
                  state.setRawData(first);
                  state.setShowRawData(true);
                  state.setError(null);
                  // Validate parsed data
                  const validation = state.validateParsedData?.(first);
                  if (validation) {
                    state.setValidationStatus(validation);
                  }
                }
              }
            }}
            parsedPdfs={state.parsedPdfs}
            scanProgress={{
              avgMsPerPdf: state.scanAvgMsPerPdf,
              currentFile: state.currentScanFile,
              elapsedMs: state.scanElapsedMs,
              isScanning: state.isScanning,
              pdfsPerSec: state.scanPps,
              progress: state.scanProgress,
              startTimestamp: state.scanStartTs,
              total: state.scanTotal,
            }}
            scanResults={state.scanResults}
            securitiesDataMap={state.securitiesDataMap}
          />
        ) : null}

        {/* PDF Upload Area */}
        <PdfUploadArea
          fileInputKey={state.fileInputKey}
          isDragOver={state.isDragOver}
          isProcessing={state.isProcessing}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onFileSelect={handleFileSelect}
          onProcessPdf={() => {
            void processPDF();
          }}
          onProcessPdfRaw={() => {
            void processPDFRaw();
          }}
          onReset={resetState}
          uploadedFile={state.uploadedFile}
        />

        {/* Progress Display */}
        {state.isScanning ? (
          <div className="mt-4 text-blue-400 bg-blue-500/10 p-3 rounded-lg border border-blue-500/30">
            <div className="flex items-center gap-3">
              <div className="animate-spin w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full" />
              <div>
                <div className="font-medium">Scanning PDFs...</div>
                <div className="text-sm text-blue-300">
                  {state.currentScanFile ? `📄 ${state.currentScanFile}` : null}
                </div>
                <div className="text-xs text-blue-400 mt-1">
                  Progress: {state.scanProgress} / {state.scanTotal || "?"} PDFs
                </div>
                <div className="text-xs text-blue-300 mt-1">
                  t = {(state.scanElapsedMs / 1000).toFixed(1)}s | {state.scanPps.toFixed(2)} PDFs/s
                  | {(state.scanAvgMsPerPdf / 1000).toFixed(2)} s/PDF
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Error Display */}
        <ErrorDisplay
          allErrorPdfs={state.allErrorPdfs}
          currentErrorIndex={state.currentErrorIndex}
          error={state.error}
          onNextError={() => {
            void navigateToNextError();
          }}
          onOpenPdf={(filePath) => {
            void openPdfFile(filePath);
          }}
          onPreviousError={() => {
            void navigateToPreviousError();
          }}
          {...(state.parsedData ? { parsedData: state.parsedData } : {})}
        />

        {/* Success Message */}
        {state.parsedData?.success ? (
          <div className="mt-4 text-green-400 bg-green-500/10 p-3 rounded-lg border border-green-500/30">
            ✅ {state.parsedData.message}
            <div className="text-sm text-green-300 mt-2">
              Geparste PDFs: {state.parsedData.successCount} | Fehlerhafte:{" "}
              {state.parsedData.errorCount} | Gesamt: {state.parsedData.totalPdfs}
            </div>
          </div>
        ) : null}

        {/* Raw PDF Text Display */}
        <RawPdfTextDisplay rawPdfText={state.rawPdfText} />

        {/* Raw Data Debug Display */}
        <RawDataDebugDisplay
          formatNumber2={formatNumber2}
          isEmpty={isEmpty}
          isLoadingSecurities={state.isLoadingSecurities}
          onClose={() => {
            state.setShowRawData(false);
          }}
          rawData={state.rawData}
          rawPdfText={state.rawPdfText}
          securitiesDataMap={state.securitiesDataMap}
          showRawData={state.showRawData}
          uploadedFile={state.uploadedFile}
          validationStatus={state.validationStatus}
        />

        {/* Parsed Data Display */}
        <ParsedDataDisplay parsedData={state.parsedData} />
      </div>
    </div>
  );
};
